import React, { useCallback, useEffect, useState } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import { translationUpdate } from 'redux/actions/users';

import { useAppDispatch } from 'redux/store';
import { getCurrentUser } from 'redux/selectors/users';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { closeEditTranslationComponent } from 'redux/actions/edit-translations';
import { getLectureComponent } from 'redux/selectors/timeline';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import { css } from '@emotion/react';
import { doubleSpacing, halfSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import { gray5, white } from 'styles/global_defaults/colors';


type EditableProps = {
  editInfo: TranslationInfo,
  containerClass: string,
  onSaveRef: (fn: () => void) => void,
  setSavingDisabled: (boolean) => void,
};

const EditablePoll = ({
  editInfo,
  containerClass,
  onSaveRef,
  setSavingDisabled,
}: EditableProps) => {
  const lectureComponent = useSelector((state) => (
    getLectureComponent(state, editInfo.id)
  )) as NLectureComponent<ComponentType.POLL>;
  const currentPoll = useSelector((state) => state.models.polls[lectureComponent.poll]);
  const currentQuestions = useSelector((state) => (
    currentPoll.questionIds?.map((questionId) => {
      const { questionText, translationIds: qTranslationIds } = state.models.pollQuestions[questionId];
      const currentQuestion = {
        question: {
          id: questionId,
          questionText,
          translationIds: qTranslationIds,
        },
        options: state.models.pollQuestions[questionId].responseOptionIds?.map((optionId) => {
          const { optionContent, translationIds: oTranslationIds } = state.models.pollOptions[optionId];
          return {
            id: optionId,
            optionContent,
            translationIds: oTranslationIds,
          };
        }),
      };
      return currentQuestion;
    })
  ));
  const initialState = {
    activity: {
      title: currentPoll.title,
    },
    questions: currentQuestions,
  };
  const [poll, setPoll] = useState(initialState);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useAppDispatch();

  const editablePollStyle = css`
    width: 100%;
    .border-simple-text {
      border-radius: ${quarterSpacing}px;
      border-color: ${gray5};
    }
    margin: 0 ${doubleSpacing}px;
    .bs4-form-control {
      background: ${white};
    }
    .tag {
      margin: ${halfSpacing}px 0;
      padding: 3px 12px 3px;
      border-radius: ${halfSpacing}px;
      height: ${standardSpacing}px;
      width: fit-content;
    }
    .options-list {
      div:not(:first-of-type) {
        margin-top: ${standardSpacing}px;
      }
    }
  `;

  const save = useCallback(() => {
    const attributes = [];
    let name;
    if (poll.activity.title !== initialState.activity.title) {
      name = 'title';
      attributes.push({
        translationId: currentPoll.translationIds[name],
        name,
        text: poll.activity.title,
        elementType: ComponentType.POLL,
        elementId: currentPoll.id,
      });
    }
    if (poll.questions !== initialState.questions) {
      const theOnlyOneQuestion = poll.questions[0]; // Because even if the parameter is in plural, polls only have one question
      if (theOnlyOneQuestion.question.questionText !== initialState.questions[0].question.questionText) {
        name = 'questionText';
        attributes.push({
          translationId: theOnlyOneQuestion.question.translationIds[name],
          name,
          text: theOnlyOneQuestion.question.questionText,
          elementType: ComponentType.POLL,
          elementId: theOnlyOneQuestion.question.id,
        });
      }
      if (theOnlyOneQuestion.options !== initialState.questions[0].options) {
        theOnlyOneQuestion.options.forEach((option, index) => {
          if (option.optionContent !== initialState.questions[0].options[index].optionContent) {
            name = 'optionContent';
            attributes.push({
              translationId: option.translationIds[name],
              name,
              text: option.optionContent,
              elementType: ComponentType.POLL,
              elementId: option.id,
            });
          }
        });
      }
    }
    const payload = {
      id: lectureComponent.id,
      attributes,
    };
    dispatch(translationUpdate(payload)).then(() => {
      dispatch(closeEditTranslationComponent());
    });
  }, [poll, currentUser.translationPreferenceLanguage, dispatch, editInfo]);

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (initialState !== poll) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
    });
  }, [poll]);

  const setQuestionText = (questionIndex, newText) => {
    const newQuestions = [...poll.questions];
    newQuestions[questionIndex] = {
      ...newQuestions[questionIndex],
      question: {
        ...newQuestions[questionIndex].question,
        questionText: newText,
      },
    };
    setPoll({
      ...poll,
      questions: newQuestions,
    });
  };

  const setOptionText = (questionIndex, optionIndex, newText) => {
    const newQuestions = [...poll.questions];
    newQuestions[questionIndex].options[optionIndex] = {
      ...newQuestions[questionIndex].options[optionIndex],
      optionContent: newText,
    };
    setPoll({
      ...poll,
      questions: newQuestions,
    });
  };

  return (
    <div className='editable-poll' css={editablePollStyle}>
      <div className='tag bg-gray-6 text-small font-weight-bolder'>
        { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TITLE() }
      </div>
      <NvTextArea
        value={poll.activity.title}
        autoComplete='off'
        textareaClassName='text-large-body border-simple-text'
        onChange={(e) => { setPoll({ ...poll, activity: { title: e.currentTarget.value } }); }}
      />
      <div className='questions'>
        {
          poll.questions.map((question, questionIndex) => (
            <div className='question'>
              <div className='tag bg-gray-6 text-small font-weight-bolder'>
                { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.QUESTION() }
              </div>
              <NvTextArea
                value={question.question.questionText}
                autoComplete='off'
                textareaClassName='text-large-body border-simple-text'
                onChange={(e) => { setQuestionText(questionIndex, e.currentTarget.value); }}
              />
              <div className='options'>
                <div className='tag bg-gray-6 text-small font-weight-bolder'>
                  {t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.OPTIONS()}
                </div>
                <div className='options-list'>
                  {
                    question.options?.map((option, optionIndex) => (
                      <NvTextArea
                        value={option.optionContent}
                        autoComplete='off'
                        textareaClassName='text-large-body border-simple-text'
                        onChange={(e) => { setOptionText(questionIndex, optionIndex, e.currentTarget.value); }}
                      />
                    ))
                  }
                </div>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
};

export default EditablePoll;
